/**
 * 按压反馈。
 *
 * 这是"丝滑"里最容易被忽略、但触感收益最大的一环：
 * 手指按下时元素缩一点，松手时带一点回冲弹回原位。
 * 没有这个，所有点击都像在点一张贴纸。
 *
 * 实现要点：
 *   · 只动 transform 的 scale，走合成层
 *   · 用 pointerdown 而不是 click，按下就有反馈，不用等抬起
 *   · pointercancel / pointerleave 必须处理，否则手指滑出去元素会卡在缩小状态
 *   · disabled 状态不参与，否则灰按钮也会缩，语义是错的
 */

import { useEffect, useRef, type RefObject } from 'react';

import { MOTION, gsap, prefersReducedMotion } from './gsap';

export interface PressOptions {
	/** 按下时缩到多少，默认 0.965 */
	scale?: number;
	/** 按下时的不透明度，默认 1（不变） */
	dim?: number;
	/** 关掉缩放，只做透明度（用于整行等大面积元素） */
	scaleOnly?: boolean;
}

export function usePress<T extends HTMLElement = HTMLElement>(
	options: PressOptions = {},
): RefObject<T> {
	const { scale = 0.965, dim = 1, scaleOnly = false } = options;
	const ref = useRef<T | null>(null);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		const set = (vars: gsap.TweenVars): void => {
			gsap.set(element, vars);
		};
		let pressed = false;

		const press = (): void => {
			if (pressed) return;
			pressed = true;
			if (prefersReducedMotion()) {
				set({ opacity: dim });
				return;
			}
			gsap.to(element, {
				scale: scaleOnly ? 1 : scale,
				opacity: dim,
				duration: MOTION.pressIn,
				ease: MOTION.ease.soft,
				overwrite: 'auto',
			});
		};

		const release = (): void => {
			if (!pressed) return;
			pressed = false;
			if (prefersReducedMotion()) {
				set({ opacity: 1 });
				return;
			}
			gsap.to(element, {
				scale: 1,
				opacity: 1,
				duration: MOTION.pressOut,
				ease: MOTION.ease.back,
				overwrite: 'auto',
				clearProps: 'transform',
			});
		};

		const isDisabled = (): boolean =>
			element instanceof HTMLButtonElement && element.disabled;

		const onDown = (): void => {
			if (isDisabled()) return;
			press();
		};

		element.addEventListener('pointerdown', onDown);
		element.addEventListener('pointerup', release);
		element.addEventListener('pointercancel', release);
		element.addEventListener('pointerleave', release);
		// 键盘用户按下空格/回车时也应该有反馈
		element.addEventListener('keydown', (event) => {
			if (event.key === ' ' || event.key === 'Enter') press();
		});
		element.addEventListener('keyup', release);
		element.addEventListener('blur', release);

		return () => {
			element.removeEventListener('pointerdown', onDown);
			element.removeEventListener('pointerup', release);
			element.removeEventListener('pointercancel', release);
			element.removeEventListener('pointerleave', release);
			element.removeEventListener('blur', release);
			gsap.killTweensOf(element);
		};
	}, [scale, dim, scaleOnly]);

	return ref as RefObject<T>;
}
